import { useEffect, useMemo, useState } from 'react';
import {
  DELIVERY_STATUS_LABELS,
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_STATUSES,
  centsToPesos,
  type DeliveryStatus,
  type TransactionStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { ReceivingSheet } from './ReceivingSheet';

export interface TransactionDto {
  id: string;
  number: string;
  branch: { id: string; code: string };
  status: TransactionStatus;
  customerName: string;
  customerPhone: string | null;
  itemCount: number;
  weightGrams: number | null;
  service: { id: string | null; name: string; unit: string; unitPriceCents: number };
  baseCents: number;
  addonCents: number;
  totalCents: number;
  paymentMethod: string | null;
  amountPaidCents: number;
  paymentStatus: 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';
  fulfillmentMethod: 'PICKUP' | 'DELIVERY';
  deliveryAddress: string | null;
  expectedAt: string | null;
  notes: string | null;
  items: { description: string; quantity: number }[];
  addons: { name: string; unitPriceCents: number; quantity: number; lineCents: number }[];
  createdBy: { name: string } | null;
  createdAt: string;
  updatedAt: string;
  /** Phase 2F - delivery workflow state; null for pick-up transactions. */
  deliveryStatus: DeliveryStatus | null;
  deliveryAssignee: string | null;
  deliveryAssignedAt: string | null;
  deliveryStartedAt: string | null;
  deliveryDeliveredAt: string | null;
  deliveryClosedAt: string | null;
  /** Phase 2D - list enrichment: when the order entered READY (queue views). */
  readyAt?: string | null;
  /** Phase 2D - list enrichment: a completed processing step recorded a count discrepancy. */
  hasDiscrepancy?: boolean;
}

export const STATUS_TONE: Record<TransactionStatus, 'slate' | 'indigo' | 'amber' | 'emerald' | 'rose'> = {
  PENDING: 'slate',
  WASHING: 'indigo',
  FOLDING: 'amber',
  READY: 'emerald',
  RELEASED: 'indigo',
  DONE: 'slate',
};

export function StatusBadge({ status }: { status: TransactionStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{TRANSACTION_STATUS_LABELS[status]}</Badge>;
}

/** Phase 2F - delivery state tone (separate from the laundry status badge). */
export const DELIVERY_STATUS_TONE: Record<
  DeliveryStatus,
  'slate' | 'indigo' | 'amber' | 'emerald' | 'rose'
> = {
  PENDING: 'slate',
  ASSIGNED: 'indigo',
  OUT_FOR_DELIVERY: 'amber',
  DELIVERED: 'emerald',
  DELIVERY_DONE: 'slate',
};

export function DeliveryStatusBadge({ status }: { status: DeliveryStatus | null }) {
  if (!status) return null;
  return <Badge tone={DELIVERY_STATUS_TONE[status]}>{DELIVERY_STATUS_LABELS[status]}</Badge>;
}

function TransactionDetailModal({ tx, onClose }: { tx: TransactionDto; onClose: () => void }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Customer', value: tx.customerName },
    { label: 'Contact', value: tx.customerPhone ?? '—' },
    { label: 'Branch', value: tx.branch.code },
    { label: 'Service', value: `${tx.service.name} @ ${centsToPesos(tx.service.unitPriceCents)}${tx.service.unit === 'KG' ? '/kg' : tx.service.unit === 'PIECE' ? '/pc' : ''}` },
    { label: 'Items', value: String(tx.itemCount) },
    ...(tx.weightGrams ? [{ label: 'Weight', value: `${(tx.weightGrams / 1000).toString()} kg` }] : []),
    { label: 'Base', value: centsToPesos(tx.baseCents) },
    ...(tx.addonCents > 0 ? [{ label: 'Add-ons', value: centsToPesos(tx.addonCents) }] : []),
    { label: 'Total', value: centsToPesos(tx.totalCents) },
    {
      label: 'Payment',
      value: `${tx.paymentMethod ?? '—'} · ${centsToPesos(tx.amountPaidCents)} (${tx.paymentStatus})`,
    },
    { label: 'Fulfillment', value: tx.fulfillmentMethod === 'DELIVERY' ? `Delivery${tx.deliveryAddress ? ` — ${tx.deliveryAddress}` : ''}` : 'Pickup' },
    { label: 'Expected', value: tx.expectedAt ? new Date(tx.expectedAt).toLocaleString('en-PH') : '—' },
    { label: 'Received by', value: tx.createdBy?.name ?? '—' },
    { label: 'Received', value: new Date(tx.createdAt).toLocaleString('en-PH') },
  ];

  return (
    <Modal title={tx.number} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={tx.status} />
          <Badge>{tx.branch.code}</Badge>
          <Badge tone={tx.paymentStatus === 'PAID' ? 'emerald' : tx.paymentStatus === 'PARTIAL' ? 'amber' : 'rose'}>
            {tx.paymentStatus}
          </Badge>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="flex justify-between gap-3 border-b border-slate-100 pb-2">
              <dt className="text-slate-500">{row.label}</dt>
              <dd className="text-right font-medium text-slate-900">{row.value}</dd>
            </div>
          ))}
        </dl>

        {tx.items.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Item breakdown</h3>
            <ul className="mt-2 space-y-1">
              {tx.items.map((item) => (
                <li key={item.description} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span>{item.description}</span>
                  <span className="font-semibold">{item.quantity}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tx.addons.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Additional services</h3>
            <ul className="mt-2 space-y-1">
              {tx.addons.map((addon) => (
                <li key={addon.name} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span>
                    {addon.name} × {addon.quantity}
                  </span>
                  <span className="font-semibold">{centsToPesos(addon.lineCents)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tx.notes && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Notes</h3>
            <p className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{tx.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

const autoWidthCls = inputCls.replace('w-full', '');

export function TransactionsPage() {
  const { branchFilter } = useAuth();
  const [txns, setTxns] = useState<TransactionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | TransactionStatus>('ALL');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [detail, setDetail] = useState<TransactionDto | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (statusFilter !== 'ALL') params.set('status', statusFilter);
    if (query) params.set('q', query);

    void api
      .get<{ items: TransactionDto[] }>(`/transactions?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setTxns(res.items);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load transactions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, statusFilter, query, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const summary = useMemo(
    () => ({
      count: txns.length,
      items: txns.reduce((sum, t) => sum + t.itemCount, 0),
      total: txns.reduce((sum, t) => sum + t.totalCents, 0),
    }),
    [txns],
  );

  return (
    <section className="space-y-4" aria-label="Transactions">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Transactions</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Receiving and history, oldest first.
            {branchFilter !== 'ALL' && ` Showing ${branchFilter}.`}
          </p>
        </div>
        <Button size="lg" onClick={() => setReceiving(true)}>
          <Icon name="plus" className="h-5 w-5" />
          New transaction
        </Button>
      </div>

      {error && <ErrorBanner message={error} />}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-55 flex-1">
            <Icon
              name="search"
              className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search number or customer…"
              aria-label="Search transactions"
              className={`${inputCls} pl-10`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'ALL' | TransactionStatus)}
            aria-label="Filter by status"
            className={`${autoWidthCls} w-auto`}
          >
            <option value="ALL">All statuses</option>
            {TRANSACTION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {TRANSACTION_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        {!loading && !error && txns.length > 0 && (
          <p className="mt-3 text-xs font-medium text-slate-500">
            {summary.count} transaction{summary.count === 1 ? '' : 's'} · {summary.items} items ·{' '}
            {centsToPesos(summary.total)}
          </p>
        )}
      </Card>

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading transactions…
        </div>
      ) : txns.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="transactions" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">No transactions yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Tap <span className="font-semibold">New transaction</span> to receive the first order.
          </p>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txns.map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDetail(tx)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-900">{tx.number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{tx.customerName}</p>
                      <p className="text-xs text-slate-500">{tx.service.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{tx.branch.code}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{tx.itemCount}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{centsToPesos(tx.totalCents)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(tx.createdAt).toLocaleDateString('en-PH')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      {detail && <TransactionDetailModal tx={detail} onClose={() => setDetail(null)} />}
      {receiving && (
        <ReceivingSheet
          onClose={() => setReceiving(false)}
          onCreated={() => {
            refresh();
          }}
        />
      )}
    </section>
  );
}
